<script setup>
/**
 * The flight shortlist an agent sent, as the agent sees it.
 *
 * The source badge is rendered on every option for the same reason it is on the search
 * screen: the same physical flight arrives as GDS content and as an NDC offer at
 * different fares, with different rules and different servicing after sale. The customer
 * is choosing between them, so the agent has to be able to see which one they chose.
 *
 * The selected option is read from the message's own content attributes, which the
 * inbound reply updates - so the bubble shows the answer without the agent scrolling for
 * the activity line.
 *
 * "Search again" reopens the shortlist dialog on THIS quote's brief. It carries the brief
 * and not the offers, and that is the whole point: a provider offer id is scoped to the
 * search response that produced it, so re-sending a stored one would quote the customer a
 * fare that may no longer exist. The agent gets back to where they started in one click
 * and re-searches, which is the only honest version of "those exact options".
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SendFlightOptionsModal from 'dashboard/components/widgets/conversation/travel/SendFlightOptionsModal.vue';

const { contentAttributes, conversationId } = useMessageContext();
const { isCloudFeatureEnabled } = useAccount();

const showModal = ref(false);

const { t } = useI18n();

const SOURCE_STYLES = {
  gds: 'bg-n-slate-3 text-n-slate-12',
  ndc: 'bg-n-teal-3 text-n-teal-11',
  merged: 'bg-n-amber-3 text-n-amber-11',
};

const quote = computed(() => contentAttributes.value?.data || {});

const options = computed(() => quote.value.options || []);

const requirements = computed(() => quote.value.requirements || {});

const routeLabel = computed(() =>
  t('TRAVEL.QUOTE.ROUTE', {
    from: requirements.value.origin,
    to: requirements.value.destination,
    date: requirements.value.departure_date,
  })
);

const statusLabel = computed(() => {
  if (quote.value.selected_key) {
    return t('TRAVEL.QUOTE.SELECTED', { option: quote.value.selected_key });
  }
  return t('TRAVEL.QUOTE.AWAITING');
});

// The brief has to have survived on the message for this to lead anywhere, and older
// quotes predate it - so the action hides rather than opening an empty dialog.
const canSearchAgain = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.TRAVEL) &&
    Boolean(requirements.value.origin && requirements.value.destination)
);
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="travel-quote">
    <p class="text-sm font-medium">{{ routeLabel }}</p>
    <ul class="flex flex-col gap-2 mt-3">
      <li
        v-for="option in options"
        :key="option.key"
        class="flex items-start gap-2 px-3 py-2 rounded-lg bg-n-alpha-black2"
        :class="{
          'ring-1 ring-n-teal-8': option.key === quote.selected_key,
        }"
      >
        <span
          class="px-2 py-0.5 text-xs font-medium uppercase rounded-full shrink-0"
          :class="SOURCE_STYLES[option.source]"
        >
          {{ t(`TRAVEL.QUOTE.SOURCE.${String(option.source).toUpperCase()}`) }}
        </span>
        <span class="text-sm">{{ option.summary }}</span>
      </li>
    </ul>
    <div class="flex items-center justify-between gap-2 mt-3">
      <p class="text-xs italic text-n-slate-11">{{ statusLabel }}</p>
      <Button
        v-if="canSearchAgain"
        xs
        ghost
        slate
        :label="t('TRAVEL.QUOTE.SEARCH_AGAIN')"
        @click="showModal = true"
      />
    </div>
    <SendFlightOptionsModal
      v-if="showModal"
      :conversation-id="conversationId"
      :initial-brief="requirements"
      @close="showModal = false"
    />
  </BaseBubble>
</template>
